import { addMinutes, format } from 'date-fns';

import { utcToLocalDateTime } from 'hooks/useDateTime';
import { appTimeFormat } from 'utils/const';
import { formatMilitaryToAMPM } from '../utils/times';

const weekdayIndex = {
  Sunday: 0,
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
};

// UTC weekday + time need a UTC date on that weekday (class start is only an anchor).
const utcDateForWeekday = (dayName, anchorDate) => {
  const target = weekdayIndex[dayName];
  if (target === undefined) return null;

  const anchor = anchorDate
    ? new Date(`${String(anchorDate).slice(0, 10)}T00:00:00Z`)
    : new Date();
  if (Number.isNaN(anchor.getTime())) return null;

  const delta = (target - anchor.getUTCDay() + 7) % 7;
  const date = new Date(anchor.getTime() + delta * 24 * 60 * 60 * 1000);

  return date.toISOString().slice(0, 10);
};

const toTimeField = (localDate, fallbackHHmm) => {
  if (localDate) {
    const value = format(localDate, appTimeFormat.H_24);

    return { label: formatMilitaryToAMPM(value), value };
  }

  if (!fallbackHHmm) return { label: '', value: '' };

  const value = String(fallbackHHmm).slice(0, 5);

  return { label: formatMilitaryToAMPM(value), value };
};

const toRecurringRow = (slot, anchorDate) => {
  const utcDate = utcDateForWeekday(slot.day, anchorDate);
  const localStart = utcDate ? utcToLocalDateTime(slot.start, utcDate) : null;
  let localEnd = utcDate ? utcToLocalDateTime(slot.end, utcDate) : null;

  if (localStart && localEnd && localEnd <= localStart) {
    localEnd = addMinutes(localEnd, 24 * 60);
  }

  const day = localStart ? format(localStart, 'EEEE') : slot.day;
  if (!day) return null;

  return {
    dayOfWeek: { label: day, value: day },
    startTime: toTimeField(localStart, slot.start),
    endTime: toTimeField(localEnd, slot.end),
  };
};

const uniqueRows = (slots, anchorDate) => {
  const seenSlots = new Set();

  return slots.reduce((rows, slot) => {
    const row = toRecurringRow(slot, anchorDate);
    if (!row) return rows;

    const slotKey = `${row.dayOfWeek.value}|${row.startTime.value}|${row.endTime.value}`;
    if (seenSlots.has(slotKey)) return rows;

    seenSlots.add(slotKey);
    rows.push(row);
    return rows;
  }, []);
};

export const scheduleDefaultValuesModel = ({
  classroom_id,
  start,
  end,
  weeklySchedule,
  userLocalFormattedWeeklySchedule,
  isExcludeClosures,
}) => {
  const source =
    Array.isArray(weeklySchedule) && weeklySchedule.length
      ? weeklySchedule
      : userLocalFormattedWeeklySchedule;
  const slots = Object.values(source || []).filter((slot) => !slot.is_custom);
  const recurring = uniqueRows(slots, start);

  return {
    classRoomId: classroom_id,
    startDate: start,
    endDate: end,
    recurring,
    excludeClosures: isExcludeClosures,
  };
};
